import { useLoadingDialog } from '@rhg/hooks/use-loading-dialog';
import commonMessages from '@rhg/lang/messages/common';
import { getScreenName } from '@rhg/libs/analytics/track-page-nav';
import { useAnalyticsTitle } from '@rhg/libs/analytics/with-analytics/use-analytics-title';
import useAuth from '@rhg/libs/auth0/use-auth';
import { Toolbar } from '@rhg/libs/material';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { PropsWithChildren, ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import Header from '../header';
import MainContainer from '../main-container';
import SkipToMain from '../skip-to-main';

interface PageProps {
    children: ReactNode;
    isWithoutHeader?: boolean;
}

interface MainContentWrapperProps {
    isWithoutHeader: boolean;
    title?: string;
}

const MainContentWrapper = ({
    isWithoutHeader,
    title,
    children,
}: PropsWithChildren<MainContentWrapperProps>) => {
    if (isWithoutHeader) {
        return <>{children}</>;
    }
    return <MainContainer title={title}>{children}</MainContainer>;
};

const Page = ({ children, ...otherProps }: PageProps) => {
    const { formatMessage } = useIntl();
    const {
        isOpen: isLoadingDialogOpen,
        isInterstitial: isLoadingDialogInterstitial,
    } = useLoadingDialog();
    const { pathname, query } = useRouter();

    const { isAuthenticated } = useAuth();

    const trackScreen = useMemo(
        () =>
            getScreenName(
                { pathname, query },
                { IsAuthenticated: isAuthenticated }
            ),
        // TODO: fix this eslint-disable
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pathname, query]
    );

    const { analyticsTitleProps } = useAnalyticsTitle(
        `${trackScreen}  |  View${trackScreen}`
    );
    const title = analyticsTitleProps?.title;

    if (isLoadingDialogOpen && isLoadingDialogInterstitial) {
        return null;
    }

    return (
        <>
            <Head>
                <title>
                    {formatMessage(commonMessages['common.brand_name'])}
                </title>
                <link rel="icon" href="/favicon.ico" />
            </Head>
            <SkipToMain />
            {!otherProps?.isWithoutHeader && (
                <>
                    <Header />
                    <Toolbar />
                </>
            )}
            <MainContentWrapper
                isWithoutHeader={!!otherProps.isWithoutHeader}
                title={title}
            >
                {children}
            </MainContentWrapper>
        </>
    );
};

export default Page;
